import { useConfig } from '../config.js';
import { explainParam } from '../../../shared/param-explainers.js';
import { tankSubtype } from '../../../shared/tank-subtype.js';
import { getDisplayUnits, formatTemp } from '../units.js';
import { readSalinityUnit, formatSalinityRange, endSentence } from '../salinity.js';
import { isGradedParam, isQuarantineTank } from '../../../shared/tank-purpose.js';
import { inRangeAction } from '../../../shared/sensei-action.js';
import { paramStatusFor, paramDeltaLabel } from '../params.js';
import { envelopeBreached } from '../param-envelope.js';
import { assessFreshnessClient, currentReadingsClient, outrankedTridentClient } from '../utils.js';

const PHASE_LABEL = {
  setup: 'Setting up',
  cycling: 'Cycling',
  maturing: 'Maturing',
  growing: 'Growing',
  established: 'Established',
};

// The range shown here is the one the card's pill is judged against, in
// the keeper's own display units, so the explanation and the pill can
// never disagree.
export function rangeLabel(key, p, tank, salinityUnit = readSalinityUnit()) {
  const [lo, hi] = Array.isArray(p?.acceptable) ? p.acceptable.map(Number) : [];
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (key === 'temp') {
    const unit = getDisplayUnits(tank).temp;
    return `${formatTemp(lo, unit, { withUnit: false })} to ${formatTemp(hi, unit)}`;
  }
  // A 34.7-35.3 ppt band read "1.026 to 1.026 S.G.." on S.G.: four places
  // when three collapse it, and one full stop.
  if (key === 'salinity') return formatSalinityRange(lo, hi, salinityUnit);
  return `${lo} to ${hi}${p.unit ? ` ${p.unit}` : ''}`;
}

// How to move a reading that sits inside the range but off its target
// (feedback #71, 2026-10-15: "How do I bring up the ph", asked from Home
// with the card reading "0.25 under" and nothing saying how). Judged the
// way the card judges it: a reading the card would not trust, or one off
// the range (the card already shows its fix), gets nothing here. Nor does
// one the card notes as outside the published range ("Not scored as
// passing"), or a range whose target sits outside it: following the
// advice there would take the reading off the range.
export function towardTarget({ tank, paramKey, param, readings }) {
  const series = currentReadingsClient(readings || [], paramKey);
  const latest = series[0];
  const target = Number(param?.target);
  if (!latest || !Number.isFinite(target) || !isGradedParam(tank, paramKey)) return null;
  if (assessFreshnessClient(series, paramKey)?.stale) return null;
  if (outrankedTridentClient(readings || [], paramKey)) return null;
  if (paramStatusFor(tank, paramKey, latest.value) !== 'ok') return null;
  if (envelopeBreached(paramKey, latest.value, tankSubtype(tank))) return null;
  const [lo, hi] = Array.isArray(param?.acceptable) ? param.acceptable.map(Number) : [];
  if (!(target >= lo && target <= hi)) return null;
  const delta = paramDeltaLabel(paramKey, latest.value, target);
  if (!delta || delta === 'on target') return null;
  const direction = Number(latest.value) < target ? 'low' : 'high';
  const text = inRangeAction(paramKey, direction, tank);
  if (!text) return null;
  return { title: direction === 'low' ? 'How to raise it' : 'How to lower it', text };
}

export function ParamExplainerSheet({ tank, paramKey, param, readings = [], onClose }) {
  const config = useConfig();
  // A QT's "cycling" means fish in a new tank, where ammonia is a danger,
  // not the cycle working: the reef phase copy would say the opposite.
  const phase = isQuarantineTank(tank) ? null : tank?.phase || null;
  const ex = explainParam(paramKey, {
    phase,
    subtype: tankSubtype(tank),
    cfg: config?.params?.explainers || null,
  });
  if (!ex) return null;
  const label = param?.label || paramKey;
  const phaseLabel = PHASE_LABEL[phase] || null;
  const range = rangeLabel(paramKey, param, tank);
  const toward = towardTarget({ tank, paramKey, param, readings });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`What is ${label}?`}
        onClick={(e) => e.stopPropagation()}
        className="slide-in"
        style={{
          width: '100%',
          maxWidth: 448,
          maxHeight: '88vh',
          overflowY: 'auto',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: '18px 22px max(28px, calc(env(safe-area-inset-bottom) + 16px))',
          background: 'var(--sheet-bg)',
          borderTop: '1px solid var(--surface-border)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            margin: '0 auto 16px',
            background: 'var(--surface-border)',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <h3
            className="font-heading"
            style={{ fontSize: 15, letterSpacing: '0.15em', textTransform: 'uppercase', margin: 0 }}
          >
            What is {label}?
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="hdr-gear"
            style={{ flexShrink: 0 }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* The answer to "how do I bring it up" first: on a 360 x 405 phone
            the sheet shows about one block before it scrolls. */}
        {toward && <ExplainerBlock title={toward.title} text={toward.text} highlight />}
        <ExplainerBlock title="What it is" text={ex.what} />
        <ExplainerBlock title="How it gets in your tank" text={ex.source} />
        {ex.next && (
          <ExplainerBlock
            title={
              phaseLabel ? `What happens next (${phaseLabel.toLowerCase()})` : 'What happens next'
            }
            text={ex.next}
            highlight={!toward}
          />
        )}

        {range && (
          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              borderRadius: 12,
              border: '1px solid var(--surface-border)',
              fontSize: 15,
              color: 'var(--text-muted)',
            }}
          >
            Your range: <strong style={{ color: 'var(--text)' }}>{range}</strong>
            {endSentence(range).slice(range.length)} Readings outside it are flagged on the card.
            You can change it in Tank settings.
          </div>
        )}
      </div>
    </div>
  );
}

function ExplainerBlock({ title, text, highlight = false }) {
  return (
    <section
      style={{
        marginBottom: 12,
        padding: highlight ? '12px 14px' : 0,
        borderRadius: highlight ? 12 : 0,
        background: highlight ? 'var(--accent-bg)' : 'transparent',
        border: highlight ? '1px solid var(--accent-border)' : 'none',
      }}
    >
      <div
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          marginBottom: 4,
        }}
      >
        {title}
      </div>
      <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, color: 'var(--text)' }}>{text}</p>
    </section>
  );
}
